import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Fuel, Settings2, Users, Gauge, CalendarDays, Check, Car, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CarDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(searchParams.get("book") === "true");
  const [activeImage, setActiveImage] = useState(0);
  const [pickupDate, setPickupDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", location: "office" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ✅ Dynamic pricing state
  const [pricingTotal, setPricingTotal] = useState(null);
  const [pricingDays, setPricingDays] = useState(1);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [effectivePrice, setEffectivePrice] = useState(null);

  // Load car + today's effective price
  useEffect(() => {
    axios.get(`${API}/cars/${id}`).then((res) => {
      setCar(res.data);
      // Fetch tomorrow's price (1 day) to show current seasonal rate immediately
      const today = new Date();
      const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
      const dayAfter = new Date(today); dayAfter.setDate(today.getDate() + 2);
      const fmt = (d) => d.toISOString().slice(0, 10);
      axios
        .get(`${API}/pricing/${res.data.id}/calculate?pickup_date=${fmt(tomorrow)}&return_date=${fmt(dayAfter)}`)
        .then((r) => setEffectivePrice(r.data.total))
        .catch(() => {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  // Recalculate price whenever dates change
  useEffect(() => {
    if (!pickupDate || !returnDate || !car) return;
    const pickup = format(pickupDate, "yyyy-MM-dd");
    const ret = format(returnDate, "yyyy-MM-dd");
    if (pickup >= ret) return;

    setPricingLoading(true);
    axios
      .get(`${API}/pricing/${car.id}/calculate?pickup_date=${pickup}&return_date=${ret}`)
      .then((res) => {
        setPricingTotal(res.data.total);
        setPricingDays(res.data.days);
      })
      .catch(() => {
        const days = Math.max(Math.ceil((returnDate - pickupDate) / (1000 * 60 * 60 * 24)), 1);
        setPricingTotal(days * car.price_per_day);
        setPricingDays(days);
      })
      .finally(() => setPricingLoading(false));
  }, [pickupDate, returnDate, car]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pickupDate || !returnDate || !form.name || !form.email || !form.phone) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        car_id: car.id,
        car_name: `${car.brand} ${car.model}`,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        pickup_date: format(pickupDate, "yyyy-MM-dd"),
        return_date: format(returnDate, "yyyy-MM-dd"),
        pickup_location: form.location === "office" ? "Office - Pylaea" : form.location === "airport" ? "Thessaloniki Airport" : "Hotel Delivery",
        notes: form.notes,
      });
      setSubmitted(true);
      toast.success(t("bookingForm.success"));
    } catch {
      toast.error("Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="py-20 text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </Layout>
    );
  }

  if (!car) {
    return (
      <Layout>
        <div className="py-20 text-center">
          <p className="font-dm-sans text-lg text-slate-400">Car not found</p>
          <Link to="/fleet" className="text-orange-500 hover:underline mt-4 inline-block">Back to Fleet</Link>
        </div>
      </Layout>
    );
  }

  const allImages = car
    ? [car.image_url, ...(car.images || [])].filter(Boolean)
    : [];

  // ✅ The single source of truth for displayed price
  const displayPrice = effectivePrice ?? car.price_per_day;

  return (
    <Layout>
      <SEO
        title={car ? `${car.brand} ${car.model} - Rent` : "Car Details"}
        description={car ? `Rent ${car.brand} ${car.model} in Thessaloniki. ${car.horsepower}hp, ${car.fuel_type}, ${car.transmission}. From €${displayPrice}/day.` : ""}
        image={car?.image_url}
      />
      <section className="py-8 md:py-12 bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/fleet" className="inline-flex items-center gap-1.5 font-outfit text-sm text-slate-500 hover:text-orange-500 transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> {t("nav.fleet")}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Car Info */}
            <div className="lg:col-span-3 space-y-6">
              {/* Gallery */}
              <div className="rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-sm">
                <div className="relative aspect-[4/3] bg-slate-100 max-h-[600px]">
                  <img
                    src={allImages[activeImage] || car.image_url}
                    alt={`${car.brand} ${car.model}`}
                    className="w-full h-full object-contain bg-slate-50"
                    data-testid="car-main-image"
                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=800"; }}
                  />
                  {allImages.length > 1 && (
                    <>
                      <button
                        data-testid="gallery-prev"
                        onClick={() => setActiveImage((prev) => (prev - 1 + allImages.length) % allImages.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow"
                      >
                        <ChevronLeft className="w-5 h-5 text-slate-700" />
                      </button>
                      <button
                        data-testid="gallery-next"
                        onClick={() => setActiveImage((prev) => (prev + 1) % allImages.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow"
                      >
                        <ChevronRight className="w-5 h-5 text-slate-700" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {allImages.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setActiveImage(i)}
                            className={`w-2 h-2 rounded-full transition-all ${i === activeImage ? "bg-white w-6" : "bg-white/50"}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
                {allImages.length > 1 && (
                  <div className="flex gap-2 p-3 overflow-x-auto" data-testid="gallery-thumbnails">
                    {allImages.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImage(i)}
                        className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === activeImage ? "border-orange-500" : "border-transparent opacity-60 hover:opacity-100"}`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="inline-block bg-orange-500/10 text-orange-500 text-xs font-outfit font-medium px-3 py-1 rounded-full capitalize mb-2">
                      {t(`fleet.${car.category}`) || car.category}
                    </span>
                    <h1 data-testid="car-detail-title" className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 tracking-tight">
                      {car.brand} {car.model}
                    </h1>
                  </div>
                  {/* ✅ Uses effectivePrice */}
                  <div className="text-right">
                    <span className="font-outfit font-bold text-3xl text-orange-500">€{displayPrice}</span>
                    <span className="block font-dm-sans text-sm text-slate-400">{t("fleet.perDay")}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-y border-slate-100">
                  <div className="flex items-center gap-2">
                    <Fuel className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-outfit text-xs text-slate-400 uppercase">{t("fleet.fuel")}</p>
                      <p className="font-dm-sans text-sm font-medium text-slate-700 capitalize">{t(`fleet.${car.fuel_type}`) || car.fuel_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings2 className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-outfit text-xs text-slate-400 uppercase">{t("fleet.transmission")}</p>
                      <p className="font-dm-sans text-sm font-medium text-slate-700 capitalize">{t(`fleet.${car.transmission}`) || car.transmission}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-outfit text-xs text-slate-400 uppercase">{t("fleet.seats")}</p>
                      <p className="font-dm-sans text-sm font-medium text-slate-700">{car.seats}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-outfit text-xs text-slate-400 uppercase">{t("fleet.engine")}</p>
                      <p className="font-dm-sans text-sm font-medium text-slate-700">{car.engine_cc}cc / {car.horsepower}hp</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="font-outfit font-semibold text-base text-slate-900 mb-2">{t("fleet.features")}</h3>
                  <p className="font-dm-sans text-sm text-slate-500">{car.features}</p>
                  {/* Year and mileage hidden — uncomment to restore
                  <p className="font-dm-sans text-sm text-slate-400 mt-2">
                    {t("fleet.year")}: {car.year} &middot; {t("fleet.mileage")}: {car.mileage.toLocaleString()} km
                  </p>
                  */}
                </div>

                {!showBooking && car.listing_type !== "sale" && (
                  <Button
                    data-testid="open-booking-btn"
                    onClick={() => setShowBooking(true)}
                    className="mt-6 w-full bg-orange-500 hover:bg-orange-600 text-white font-outfit font-bold rounded-full py-6 text-lg shadow-xl hover:shadow-orange-500/30 transition-all hover:-translate-y-0.5"
                  >
                    {t("fleet.bookThis")}
                  </Button>
                )}
                {car.listing_type === "sale" && (
                  <Link to="/contact">
                    <Button
                      data-testid="contact-sale-btn"
                      className="mt-6 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-outfit font-bold rounded-full py-6 text-lg shadow-xl hover:shadow-emerald-600/30 transition-all hover:-translate-y-0.5"
                    >
                      Contact Us About This Car
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {/* Booking Form */}
            {showBooking && car.listing_type !== "sale" && (
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm sticky top-24">
                  {submitted ? (
                    <div data-testid="booking-success" className="text-center py-8">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="font-outfit font-semibold text-xl text-slate-900 mb-2">{t("bookingForm.success")}</h3>
                      <Link to="/fleet">
                        <Button variant="outline" className="mt-4 rounded-full">{t("nav.fleet")}</Button>
                      </Link>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Car className="w-5 h-5 text-orange-500" />
                        <h3 className="font-outfit font-semibold text-lg text-slate-900">{t("bookingForm.title")}</h3>
                      </div>

                      {/* ✅ Uses effectivePrice */}
                      <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between">
                        <span className="font-dm-sans text-sm text-slate-600">{car.brand} {car.model}</span>
                        <span className="font-outfit font-bold text-orange-500">
                          {pricingTotal && pricingDays
                            ? `€${Math.round(pricingTotal / pricingDays)}/day`
                            : `€${displayPrice}${t("fleet.perDay")}`
                          }
                        </span>
                      </div>

                      <div>
                        <label className="block font-outfit text-xs font-medium text-slate-500 mb-1">{t("bookingForm.name")} *</label>
                        <Input data-testid="booking-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 bg-slate-50 border-slate-200 rounded-lg" required />
                      </div>
                      <div>
                        <label className="block font-outfit text-xs font-medium text-slate-500 mb-1">{t("bookingForm.email")} *</label>
                        <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 bg-slate-50 border-slate-200 rounded-lg" required />
                      </div>
                      <div>
                        <label className="block font-outfit text-xs font-medium text-slate-500 mb-1">{t("bookingForm.phone")} *</label>
                        <Input data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 bg-slate-50 border-slate-200 rounded-lg" required />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block font-outfit text-xs font-medium text-slate-500 mb-1">{t("bookingForm.pickupDate")} *</label>
                          <Popover open={pickupOpen} onOpenChange={setPickupOpen}>
                            <PopoverTrigger asChild>
                              <button type="button" data-testid="detail-pickup-date" className="w-full flex items-center gap-1.5 h-11 bg-slate-50 border border-slate-200 rounded-lg px-3 text-left font-dm-sans text-sm hover:border-orange-300 transition-colors">
                                <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                                {pickupDate ? format(pickupDate, "dd/MM/yy") : <span className="text-slate-400 text-xs">Select</span>}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar mode="single" selected={pickupDate} onSelect={(d) => { setPickupDate(d); setPickupOpen(false); }} disabled={(date) => date < new Date()} />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <label className="block font-outfit text-xs font-medium text-slate-500 mb-1">{t("bookingForm.returnDate")} *</label>
                          <Popover open={returnOpen} onOpenChange={setReturnOpen}>
                            <PopoverTrigger asChild>
                              <button type="button" data-testid="detail-return-date" className="w-full flex items-center gap-1.5 h-11 bg-slate-50 border border-slate-200 rounded-lg px-3 text-left font-dm-sans text-sm hover:border-orange-300 transition-colors">
                                <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                                {returnDate ? format(returnDate, "dd/MM/yy") : <span className="text-slate-400 text-xs">Select</span>}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar mode="single" selected={returnDate} onSelect={(d) => { setReturnDate(d); setReturnOpen(false); }} disabled={(date) => date < (pickupDate || new Date())} />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <div>
                        <label className="block font-outfit text-xs font-medium text-slate-500 mb-1">{t("bookingForm.pickupLocation")}</label>
                        <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                          <SelectTrigger data-testid="detail-location" className="h-11 bg-slate-50 border-slate-200 rounded-lg font-dm-sans text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="office">{t("booking.office")}</SelectItem>
                            <SelectItem value="airport">{t("booking.airport")}</SelectItem>
                            <SelectItem value="hotel">{t("booking.hotel")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block font-outfit text-xs font-medium text-slate-500 mb-1">{t("bookingForm.notes")}</label>
                        <Textarea data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-50 border-slate-200 rounded-lg" rows={2} />
                      </div>

                      {/* ✅ Dynamic pricing total */}
                      {pickupDate && returnDate && (
                        <div data-testid="booking-total" className="bg-orange-50 rounded-xl p-4 flex items-center justify-between">
                          <div>
                            <span className="font-dm-sans text-sm text-slate-500">{pricingDays} {t("bookingForm.days")}</span>
                            <p className="font-outfit text-xs text-slate-400">{t("bookingForm.total")}</p>
                          </div>
                          {pricingLoading ? (
                            <div className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <span className="font-outfit font-bold text-2xl text-orange-500">€{pricingTotal}</span>
                          )}
                        </div>
                      )}

                      <Button
                        type="submit"
                        data-testid="submit-booking-btn"
                        disabled={submitting}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white font-outfit font-medium rounded-full py-6 shadow-lg hover:shadow-slate-900/25 transition-all"
                      >
                        {submitting ? "..." : t("bookingForm.submit")}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
